import React, { useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';

/**
 * Generic confirmation modal — drop-in replacement for window.confirm.
 *
 * Props:
 *   show          {boolean}   — whether to render
 *   title         {string}    — bold heading
 *   message       {string}    — body text
 *   onConfirm     {function}  — called when user clicks the confirm button
 *   onCancel      {function}  — called when user cancels / closes
 *   confirmLabel  {string}    — confirm button text (default: "Tak")
 *   cancelLabel   {string}    — cancel button text  (default: "Anuluj")
 *   danger        {boolean}   — red confirm button (default: true)
 *   icon          {string}    — Font Awesome class e.g. "fas fa-trash-alt"
 */
const ConfirmModal = ({
  show,
  title = 'Czy na pewno?',
  message,
  onConfirm,
  onCancel,
  confirmLabel = 'Tak',
  cancelLabel = 'Anuluj',
  danger = true,
  icon = 'fas fa-exclamation-triangle',
}) => {
  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Escape') onCancel();
    },
    [onCancel],
  );

  useEffect(() => {
    if (show) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [show, handleKeyDown]);

  if (!show) return null;

  const confirmBg  = danger ? '#ef4444' : '#2e496b';
  const confirmHover = danger ? '#dc2626' : '#1e3a5f';
  const iconBg    = danger ? '#fef2f2' : '#eff6ff';
  const iconColor = danger ? '#ef4444' : '#2e496b';

  return ReactDOM.createPortal(
    <div
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(15, 23, 42, 0.5)',
        backdropFilter: 'blur(4px)',
        animation: 'confirmOverlayIn 0.18s ease-out',
      }}
    >
      <style>{`
        @keyframes confirmOverlayIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes confirmModalIn   { from { opacity: 0; transform: scale(0.94) translateY(-8px) } to { opacity: 1; transform: scale(1) translateY(0) } }
        .confirm-modal-confirm-btn:hover { background: ${confirmHover} !important; }
      `}</style>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: '16px',
          padding: '32px',
          width: '90%',
          maxWidth: '420px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
          textAlign: 'center',
          animation: 'confirmModalIn 0.22s ease-out',
        }}
      >
        {/* Icon */}
        <div style={{
          width: '56px', height: '56px', margin: '0 auto 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: '50%', background: iconBg, color: iconColor, fontSize: '1.4rem',
        }}>
          <i className={icon} />
        </div>

        {/* Title */}
        <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#1e293b', margin: '0 0 8px' }}>
          {title}
        </h3>

        {/* Message */}
        {message && (
          <p style={{ fontSize: '0.9rem', color: '#64748b', lineHeight: 1.55, margin: '0 0 28px' }}>
            {message}
          </p>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '10px 24px', borderRadius: '10px', border: '1px solid #e2e8f0',
              background: '#f8fafc', color: '#475569', fontWeight: 600, fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className="confirm-modal-confirm-btn"
            onClick={() => { onConfirm(); }}
            style={{
              padding: '10px 24px', borderRadius: '10px', border: 'none',
              background: confirmBg, color: '#fff', fontWeight: 600, fontSize: '14px',
              cursor: 'pointer', transition: 'background 0.15s',
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ConfirmModal;
